import React, { useState, useRef, useEffect } from 'react';
import Icon from './Icon';
import { HelpCircle, X } from 'lucide-react';
import { Z } from '../constants/tokens';

export default function PageHelp({ title, steps, theme }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const keyHandler = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(v => !v)}
        title={`Help: ${title}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          // Touch target grown from ~24px to ~34px WITHOUT changing the layout
          // footprint: the extra padding is cancelled by an equal negative
          // margin, so the icon stays exactly where it was and this shared
          // component still lines up with the size-13 icons in the tab strips
          // it is embedded in (App.js subject tabs, DashboardPanel).
          padding: '9px', margin: '-5px', display: 'flex', alignItems: 'center',
          color: theme.textSub || '#888',
          opacity: 0.7,
          transition: 'opacity 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = '1'}
        onMouseLeave={e => e.currentTarget.style.opacity = '0.7'}
      >
        <Icon icon={HelpCircle} size={16} />
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div style={{ position: 'fixed', inset: 0, zIndex: Z.dropdown }} onClick={() => setOpen(false)} />

          {/* Dropdown */}
          <div role="dialog" aria-label={title} style={{
            position: 'absolute', right: 0, top: '100%', marginTop: '4px',
            zIndex: Z.dropdown, width: '280px',
            borderRadius: '12px', padding: '14px',
            background: theme.sidebar || '#fff',
            border: `1px solid ${theme.border || '#e5e7eb'}`,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          }}>
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: '10px',
            }}>
              <span style={{
                fontWeight: 700, fontSize: '0.82rem',
                color: theme.text || '#1a1a1a',
              }}>
                {title}
              </span>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close help"
                title="Close"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: theme.textSub || '#888', display: 'flex',
                  // Same trick: ~30px tap target, unchanged 16px layout box, so
                  // the popover header keeps its height next to the 0.82rem title.
                  padding: '9px', margin: '-7px',
                }}
              >
                <Icon icon={X} size={12} />
              </button>
            </div>

            {/* Steps */}
            <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {steps.map((step, i) => (
                <li key={i} style={{
                  fontSize: '0.8rem', color: theme.textSub || '#666',
                  display: 'flex', gap: '8px', lineHeight: 1.5,
                }}>
                  <span style={{
                    flexShrink: 0, fontWeight: 700, width: '16px', textAlign: 'right',
                    color: theme.glossaryAccent || '#f59e0b',
                  }}>
                    {i + 1}.
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </>
      )}
    </div>
  );
}
